import { useState } from 'react';
import { PageWrapper } from '@src/components/Wrapper';
import { OptionSelector } from '@src/components/OptionSelector';
import { useNavigate } from 'react-router-dom';
import { ROUTE_MY_ESIMS_FULL_URL } from '@src/constants/URLS.const';
import { Apple } from 'iconsax-react';
import { Button } from 'antd';

type TabKey = 'qr' | 'manual';

interface AppleGuideProps {
  onClose: () => void;
}

export function AppleGuide({ onClose }: Readonly<AppleGuideProps>) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'qr' | 'manual'>('qr');
  const tabOptions: TabKey[] = ['qr', 'manual'];
  const handleTabChange = (key: TabKey | null) => {
    if (!key) return;
    setActiveTab(key);
  };

  const handleInstallClick = () => {
    navigate(ROUTE_MY_ESIMS_FULL_URL);
    onClose();
  };

  return (
    <PageWrapper rootClassName='bg-wl-background p-4'>
      <div className='flex justify-between bg-wl-surface rounded-lg py-4 px-8 text-wl-on-background'>
        <span>IOS</span> <Apple variant='Bold' />
      </div>
      {/* Tab Buttons */}
      <div className='flex justify-center gap-4 my-4'>
        <OptionSelector<TabKey>
          options={tabOptions}
          selected={activeTab}
          onSelect={handleTabChange}
          renderLabel={(key) => (key === 'qr' ? 'QR Code' : 'Manual')}
          className='w-full !pr-0 [&_button]:w-full'
        />
      </div>
      <Button
        className='btn-primary w-full mb-4 h-12'
        onClick={handleInstallClick}
      >
        Install eSIM
      </Button>
      <div className='p-4 rounded-xl bg-error-container text-error-light text-M-14'>
        WARNING! Most eSIMs can only be installed once. If you remove the eSIM
        from your device, you cannot install it again.
      </div>
      {/* QR Code Guide */}
      {activeTab === 'qr' && (
        <div className='max-w-2xl mx-auto p-6 bg-wl-surface text-wl-on-background rounded-lg mt-6'>
          <p className='text-B-16 mb-4'>QR Code Installation Method</p>
          <div className='mb-4'>
            <p className='text-B-16 mb-4'>Step 1/2 – Install eSIM</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>Open Settings on your iPhone.</li>
              <li>Go to Cellular / Mobile Data → Add Cellular Plan.</li>
              <li>Select Use QR Code.</li>
              <li>Scan the QR code you received.</li>
              <li>Tap Add Cellular Plan when prompted.</li>
              <li>Configure your label, default line, and data preferences.</li>
            </ol>

            <p className='mt-2 text-R-12'>
              Note: Most eSIMs can only be installed once. If you remove it, you
              cannot reinstall the same eSIM.
            </p>
          </div>
          <div>
            <p className='text-B-16 mb-4'>Step 2/2 – Access Data</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>Go to Settings → Cellular / Mobile Data.</li>
              <li>
                <span>Select the newly installed eSIM plan.</span>
                <ul className='list-disc list-inside pl-6 space-y-1'>
                  <li>Turn on This Line.</li>
                  <li>Set it as the plan for Mobile Data.</li>
                </ul>
              </li>
              <li>
                <span>[Optional – Only if you face connection issues]</span>
                <ul className='list-disc list-inside pl-6 space-y-1'>
                  <li>
                    Tap Network Selection, disable Automatic, and choose the
                    supported network manually.
                  </li>
                </ul>
              </li>
              <li>Enable Data Roaming for the new eSIM plan.</li>
            </ol>
          </div>
        </div>
      )}
      {/* Manual Guide */}
      {activeTab === 'manual' && (
        <div className='max-w-2xl mx-auto p-6 bg-wl-surface text-wl-on-background rounded-lg mt-6'>
          <p className='text-B-16 mb-4'>Manual Installation Method</p>
          <div className='mb-4'>
            <p className='text-B-16 mb-4'>Step 1/2 – Install eSIM</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>Go to Settings &gt; Cellular &gt; Add Cellular Plan.</li>
              <li>Tap Enter Details Manually.</li>
              <li>
                <span>Enter the following:.</span>
                <ul className='list-disc list-inside pl-6 space-y-1'>
                  <li>SM-DP+ Address</li>
                  <li>Activation Code</li>
                  <li>(Optional) Confirmation Code</li>
                </ul>
              </li>
              <li>Tap Next and wait for activation.</li>
              <li>Configure label and usage settings.</li>
            </ol>
          </div>
          <div className='mb-4'>
            <p className='text-B-16 mb-4'>Step 2/2 – Access Data</p>
            <ol className='list-decimal list-inside text-M-14 space-y-2 pl-4'>
              <li>Go to Settings → Cellular / Mobile Data.</li>
              <li>
                <span>Select the newly installed eSIM plan.</span>
                <ul className='list-disc list-inside pl-6 space-y-1'>
                  <li>Turn on This Line.</li>
                  <li>Set it as the plan for Mobile Data.</li>
                </ul>
              </li>
              <li>
                <span>[Optional – Only if you face connection issues]</span>
                <ul className='list-disc list-inside pl-6 space-y-1'>
                  <li>
                    Tap Network Selection, disable Automatic, and choose the
                    supported network manually.
                  </li>
                </ul>
              </li>
              <li>Enable Data Roaming for the new eSIM plan.</li>
            </ol>
          </div>
        </div>
      )}
    </PageWrapper>
  );
}
